import { TFunction } from "i18next";
import { ComponentData } from "@companion-core/shared/app/Interfaces/API";
import { useConfigStore } from "@companion-core/shared/app/Store/configStore";

export const POST_LIST_JOURNEY_DISPLAY_TYPE = "post-list-journey";

const JOURNEY_BLOCK_TYPES = new Set(["post-list-journey", "list-journey"]);

/**
 * Whether the onboarding journey is enabled for this companion.
 * @returns {boolean} Whether the onboarding module is enabled.
 */
export const isOnboardingEnabled = (): boolean =>
  useConfigStore.getState().config?.modules?.onboarding?.enabled === true;

export const isJourneyBlock = (type: string, displayType: string): boolean =>
  JOURNEY_BLOCK_TYPES.has(type) || displayType === POST_LIST_JOURNEY_DISPLAY_TYPE;

export const isJourneyPage = (blocks: { type: string; display_type?: string }[]): boolean => {
  const contentBlocks = blocks.filter((block) => block.type !== "tab-container");

  return (
    contentBlocks.length > 0 &&
    contentBlocks.every((block) => isJourneyBlock(block.type, block.display_type ?? ""))
  );
};

export const containsJourneyBlock = (blocks: { type: string; display_type?: string }[]): boolean =>
  blocks.some((block) => isJourneyBlock(block.type, block.display_type ?? ""));

export const JOURNEY_SWIPE_THRESHOLD_PX = 50;

export const shouldGoToNextSlideOnSwipe = (
  startX: number,
  endX: number,
  isLastSlide: boolean
): boolean => !isLastSlide && startX - endX >= JOURNEY_SWIPE_THRESHOLD_PX;

export const shouldGoToNextSlideOnPanDx = (dx: number, isLastSlide: boolean): boolean =>
  !isLastSlide && dx <= -JOURNEY_SWIPE_THRESHOLD_PX;

const CMS_KEY_DEFAULTS: Record<string, string> = {
  "onboarding.cta.next": "Suivant",
  "onboarding.cta.start": "Commencer",
};

export const getSlideImage = (slide: ComponentData) => slide.medias?.[0];

export const hasSlideImage = (slide: ComponentData): boolean => Boolean(getSlideImage(slide)?.url);

export const translateCmsKey = (t: TFunction, key?: string | null): string | undefined => {
  if (!key) {
    return undefined;
  }

  const translated = t(key, { defaultValue: CMS_KEY_DEFAULTS[key] ?? key });

  if (translated === key && CMS_KEY_DEFAULTS[key]) {
    return CMS_KEY_DEFAULTS[key];
  }

  return translated;
};

export const getSlideCtaLabel = (
  slide: ComponentData,
  isLastSlide: boolean,
  t: TFunction
): string => {
  const buttonTitle = "button_title" in slide ? slide.button_title : undefined;

  return (
    translateCmsKey(t, buttonTitle) ??
    (isLastSlide
      ? t("onboarding.cta.start", { defaultValue: "Commencer" })
      : t("onboarding.cta.next", { defaultValue: "Suivant" }))
  );
};

export const getSlideAccessibilityLabel = (
  slideIndex: number,
  totalSlides: number,
  t: TFunction
): string =>
  t("onboarding.journey.slide-position", {
    current: slideIndex + 1,
    total: totalSlides,
    defaultValue: `Slide ${slideIndex + 1} of ${totalSlides}`,
  });
